import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, MapPin, DollarSign, Briefcase, Check } from "lucide-react";

export default function Compare() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (!ids.length) return;
    api.post("/jobs/compare", { ids }).then((r) => setJobs(r.data.jobs));
    // eslint-disable-next-line
  }, [params.get("ids")]);

  const rows = [
    { label: "Employer", key: (j) => j.employer },
    { label: "Location", key: (j) => j.location, icon: <MapPin size={12} /> },
    { label: "Wage Range", key: (j) => `$${j.wage_min}-$${j.wage_max}/${j.wage_unit}`, icon: <DollarSign size={12} /> },
    { label: "Type", key: (j) => j.type, icon: <Briefcase size={12} /> },
    { label: "Industry", key: (j) => j.industry },
    { label: "Registered", key: (j) => (j.registered_partner ? "Yes" : "No") },
    { label: "O*NET", key: (j) => j.onet_code },
    { label: "Posted", key: (j) => j.posted_date },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/dashboard" className="text-sm text-zinc-500 hover:text-yellow-500 uppercase tracking-wider flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">Side-by-side</div>
      <h1 className="font-display text-5xl md:text-6xl uppercase leading-none mb-10">Compare <span className="text-yellow-500">jobs</span></h1>

      {jobs.length === 0 ? (
        <div className="text-zinc-500 font-mono-alt uppercase text-sm">Loading…</div>
      ) : (
        <div className="overflow-x-auto border border-zinc-800">
          <table className="w-full border-collapse min-w-[720px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-xs font-mono-alt uppercase text-zinc-500 tracking-widest w-40"></th>
                {jobs.map((j) => (
                  <th key={j.id} className="text-left p-4 border-l border-zinc-800 align-top">
                    <div className="text-xs font-mono-alt text-yellow-500 uppercase">{j.industry}</div>
                    <Link to={`/jobs/${j.id}`} data-testid={`compare-title-${j.id}`} className="font-display text-2xl uppercase leading-tight hover:text-yellow-500 block mt-1">{j.title}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b border-zinc-900">
                  <td className="p-4 text-xs font-mono-alt uppercase text-zinc-500 tracking-widest">{row.label}</td>
                  {jobs.map((j) => (
                    <td key={j.id} className="p-4 border-l border-zinc-800 text-sm text-zinc-200">
                      <span className="inline-flex items-center gap-1">{row.icon}{row.key(j)}</span>
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-b border-zinc-900">
                <td className="p-4 text-xs font-mono-alt uppercase text-zinc-500 tracking-widest">Skills</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-4 border-l border-zinc-800 text-sm text-zinc-200">
                    <div className="flex flex-wrap gap-1">
                      {j.skills.map((s) => (<span key={s} className="text-[10px] font-mono-alt uppercase tracking-wider border border-zinc-800 px-2 py-0.5">{s}</span>))}
                    </div>
                  </td>
                ))}
              </tr>
              <tr className="border-b border-zinc-900">
                <td className="p-4 text-xs font-mono-alt uppercase text-zinc-500 tracking-widest">Benefits</td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-4 border-l border-zinc-800 text-sm text-zinc-200">
                    <ul className="space-y-1">
                      {j.benefits.map((b, i) => (<li key={i} className="flex items-start gap-2"><Check size={12} className="text-yellow-500 mt-1 shrink-0" />{b}</li>))}
                    </ul>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="p-4"></td>
                {jobs.map((j) => (
                  <td key={j.id} className="p-4 border-l border-zinc-800">
                    <a href={j.apply_url} target="_blank" rel="noreferrer" className="inline-block bg-yellow-500 text-black font-bold uppercase tracking-wider text-xs px-4 py-2 hover:bg-yellow-400 transition">Apply</a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
